// src/Payroll_Management/PayRoll_Report/EmployeeJoiningReport.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Search, Printer, Building2, CalendarDays } from "lucide-react";

const BASE_URL = "https://nissi-india.com";
const MAX_PAGE_BUTTONS = 5;

function s(v) {
  return (v ?? "").toString().trim();
}
function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

function getPageWindow(current, totalPages) {
  if (totalPages <= MAX_PAGE_BUTTONS) return { start: 1, end: totalPages };
  const half = Math.floor(MAX_PAGE_BUTTONS / 2);
  let start = current - half;
  let end = current + half;

  if (start < 1) {
    start = 1;
    end = MAX_PAGE_BUTTONS;
  }
  if (end > totalPages) {
    end = totalPages;
    start = totalPages - MAX_PAGE_BUTTONS + 1;
  }
  return { start, end };
}

const EmployeeJoiningReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchid") || localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchtype") || localStorage.getItem("branchType") || "HO";

  const [loading, setLoading] = useState(false);

  // meta dropdowns
  const [transferCodes, setTransferCodes] = useState([]);
  const [fromSites, setFromSites] = useState([]);
  const [toSites, setToSites] = useState([]);

  // filters (ASP controls)
  const [trancode, setTrancode] = useState("Select");
  const [fromSite, setFromSite] = useState("Select");
  const [toSite, setToSite] = useState("Select");
  const [fromDate, setFromDate] = useState(""); // dd/MM/yyyy
  const [toDate, setToDate] = useState("");     // dd/MM/yyyy

  // autocomplete inputs (ASP txt_listempcode/txt_listempnme)
  const [empcode, setEmpcode] = useState("");
  const [empname, setEmpname] = useState("");
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);

  // grid
  const [rows, setRows] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-33", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const fetchMeta = async () => {
    if (!s(finYearId) || !s(loginBranchId) || !s(branchType)) {
      toast.warn("Missing finYearId / branchid / branchtype in localStorage");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-joining-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: branchType,
        },
        timeout: 60000,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setTransferCodes(res.data.transferCodes || []);
      setFromSites(res.data.fromSites || []);
      setToSites(res.data.toSites || []);

      setToSite(res.data.defaultToSite ? String(res.data.defaultToSite) : "Select");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = 1) => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(toSite) || toSite === "Select") {
      toast.warn("Please select To Site");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-joining-report/data`, {
        params: {
          finYearId: Number(finYearId),
          toSite,
          fromSite,
          fromDate,
          toDate,
          empcode: empcode.trim(),
          empname: empname.trim(),
          trancode,
          page: p,
          pageSize,
        },
        timeout: 60000,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load data");
        return;
      }

      setRows(res.data.rows || []);
      setTotal(toInt(res.data.total, 0));
      setTotalPages(toInt(res.data.totalPages, 1));
      setPage(toInt(res.data.page, p));
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  // initial load
  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pagination page change
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // autocomplete: empcode
  const loadEmpCodeSuggestions = async (text) => {
    const q = text.trim();
    if (!q || q.length < 1) {
      setCodeSug([]);
      return;
    }
    if (!s(toSite) || toSite === "Select") return;

    try {
      const res = await axios.get(`${BASE_URL}/api/employee-joining-report/autocomplete/empcode`, {
        params: {
          finYearId: Number(finYearId),
          toSite,
          branchType,
          q,
        },
        timeout: 30000,
      });
      if (res?.data?.success) setCodeSug(res.data.items || []);
    } catch {
      setCodeSug([]);
    }
  };

  // autocomplete: empname
  const loadEmpNameSuggestions = async (text) => {
    const q = text.trim();
    if (!q || q.length < 1) {
      setNameSug([]);
      return;
    }
    if (!s(toSite) || toSite === "Select") return;

    try {
      const res = await axios.get(`${BASE_URL}/api/employee-joining-report/autocomplete/empname`, {
        params: {
          finYearId: Number(finYearId),
          toSite,
          branchType,
          q,
        },
        timeout: 30000,
      });
      if (res?.data?.success) setNameSug(res.data.items || []);
    } catch {
      setNameSug([]);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const openPrint = (empJoinId) => {
    // later we’ll build EmployeeJoiningReportPrint page
    window.open(`/PayRoll/EmployeeJoiningReport_Print?empJoinId=${empJoinId}`, "_blank");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const { start, end } = getPageWindow(page, totalPages);
  const pageNums = [];
  for (let i = start; i <= end; i++) pageNums.push(i);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE JOINING REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* header badges */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Joining List
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search + Print + Pagination
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>
          </div>

          {/* filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

              {/* Transfer Code */}
              <div>
                <label className={labelBase}>Transfer Code</label>
                <select className={inputBase} value={trancode} onChange={(e) => setTrancode(e.target.value)}>
                  <option value="Select">Select</option>
                  {transferCodes.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.code}
                    </option>
                  ))}
                </select>
              </div>

              {/* Employee Code (autocomplete) */}
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <input
                  value={empcode}
                  onChange={(e) => {
                    setEmpcode(e.target.value);
                    loadEmpCodeSuggestions(e.target.value);
                  }}
                  className={inputBase}
                  placeholder="Type employee code..."
                />
                {codeSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg max-h-56 overflow-auto ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {codeSug.map((x, i) => (
                      <div
                        key={i}
                        className={`px-4 py-2 cursor-pointer ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
                        onClick={() => {
                          setEmpcode(x);
                          setCodeSug([]);
                        }}
                      >
                        {x}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* From Site */}
              <div>
                <label className={labelBase}>From Site</label>
                <select className={inputBase} value={fromSite} onChange={(e) => setFromSite(e.target.value)}>
                  <option value="Select">Select</option>
                  {fromSites.map((b) => (
                    <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Employee Name (autocomplete) */}
              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <input
                  value={empname}
                  onChange={(e) => {
                    setEmpname(e.target.value);
                    loadEmpNameSuggestions(e.target.value);
                  }}
                  className={inputBase}
                  placeholder="Type employee name..."
                />
                {nameSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg max-h-56 overflow-auto ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {nameSug.map((x, i) => (
                      <div
                        key={i}
                        className={`px-4 py-2 cursor-pointer ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
                        onClick={() => {
                          setEmpname(x);
                          setNameSug([]);
                        }}
                      >
                        {x}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* From Date */}
              <div>
                <label className={labelBase}>From Date (dd/MM/yyyy)</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase}
                    placeholder="e.g. 01/01/2026"
                  />
                </div>
              </div>

              {/* To Date */}
              <div>
                <label className={labelBase}>To Date (dd/MM/yyyy)</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase}
                    placeholder="e.g. 31/01/2026"
                  />
                </div>
              </div>

              {/* To Site */}
              <div>
                <label className={labelBase}>To Site</label>
                <select
                  className={inputBase}
                  value={toSite}
                  onChange={(e) => {
                    setToSite(e.target.value);
                    // also clear suggestions because contextKey depends on toSite
                    setCodeSug([]);
                    setNameSug([]);
                  }}
                >
                  <option value="Select">Select</option>
                  {toSites.map((b) => (
                    <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Joining Date</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Department</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">From Site</th>
                    <th className="px-4 py-4 text-center font-bold">Print</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 8 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={`${r.fld_employee_joiningempid}-${idx}`} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.trdate}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.code}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.empname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.depnme}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.designme}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.brnme}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => openPrint(r.fld_employee_joiningempid)}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-white text-sm font-bold shadow transform hover:-translate-y-0.5 transition-all ${primaryBtnClass} ${primaryHoverClass}`}
                          >
                            <Printer className="w-4 h-4" />
                            Print
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {pageNums.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === p
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeJoiningReport;
